import type { TypeSafeTableColumns } from "@dealer-center/core-components";
import {
  Badge,
  Button,
  HeartIcon,
  HeartSolidIcon,
  Tooltip
} from "@dealer-center/core-components";
import type { dealerSearchOutgoingHits } from "@dealer-center/ddc-pages-account-search-interface";
import { verticalTypeMapping } from "@dealer-center/ddc-pages-account-search-interface";
import styled from "styled-components";
import { AccountLink } from "@dealer-center/ddc-core-custom-components";
import type { windowSizes } from "@dealer-center/core-custom-components";
const StyledAccountLink = styled(AccountLink)`
  min-width: 6em;
`;
const StyledFavoritesButton = styled(Button)`
  padding: 0;
`;
export function getColumns(
  toggleAccountFavorite: (account: string) => void,
  favorites: readonly string[],
  currentSize: windowSizes
): TypeSafeTableColumns<dealerSearchOutgoingHits> {
  const isFavorite = (id: string) => favorites.includes(id);

  const addressColumns: TypeSafeTableColumns<dealerSearchOutgoingHits> = [
    "sm",
    "xs"
  ].includes(currentSize)
    ? [
        {
          title: "Address",
          render: (_text: string[], record: dealerSearchOutgoingHits) =>
            `${record.city}, ${record.state}, ${record.country}`
        }
      ]
    : [
        {
          title: "City",
          dataIndex: "city"
        },
        {
          title: "State",
          dataIndex: "state"
        },
        {
          title: "Country",
          dataIndex: "country"
        }
      ];

  const retval: TypeSafeTableColumns<dealerSearchOutgoingHits> = [
    {
      title: "",
      dataIndex: "id",
      width: "50px",
      render: (_text: string, record: dealerSearchOutgoingHits) => (
        <StyledFavoritesButton
          analyticsConfiguration={{
            eventName: `${isFavorite(record.id) ? "Remove" : "Add"} Favorite}`
          }}
          buttonStyle="aux"
          // Passed as `icon` rather than as children: Interstate shrinks an
          // aux button's child SVG to its 12px text size, while `icon` gets the
          // icon-button size the History and Favorites hearts use.
          icon={
            isFavorite(record.id) ? (
              <Tooltip
                position="bottom"
                size="small"
                toolTipContent="Remove favorite"
              >
                <HeartSolidIcon title="Remove favorite" />
              </Tooltip>
            ) : (
              <Tooltip
                position="bottom"
                size="small"
                toolTipContent="Add favorite"
              >
                <HeartIcon title="Add favorite" />
              </Tooltip>
            )
          }
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            toggleAccountFavorite(record.id);
          }}
        />
      )
    },
    {
      title: "Account ID",
      dataIndex: "id",
      render: (_text: string, record: dealerSearchOutgoingHits) => (
        <StyledAccountLink accountID={record.id} component="client-search" />
      )
    },
    {
      title: "Company Name",
      dataIndex: "name"
    },
    {
      title: "URL",
      dataIndex: "url"
    },
    {
      title: "Status",
      dataIndex: "status"
    },
    ...addressColumns,
    {
      title: "Vertical Type",
      dataIndex: "verticalType",
      render: (verticalType: string) =>
        verticalTypeMapping.find(({ value }) => value === verticalType)
          ?.label || verticalType
    },
    {
      title: "Account type",
      dataIndex: "type",
      render: (type: string) => <Badge variant="light">{type}</Badge>
    },
    {
      title: "Franchise",
      dataIndex: "franchiseList",
      render: (_text: string[], record: dealerSearchOutgoingHits) => (
        <Tooltip
          position="bottom"
          size="large"
          toolTipContent={record.franchiseList
            ?.map(franchise => franchise)
            .join(", ")}
        >
          <a
            href={`dealer/${record.id}/overview`}
            rel="noreferrer"
            target="_blank"
          >{`${record.franchiseList.length}`}</a>
        </Tooltip>
      )
    },
    {
      title: "Resource",
      dataIndex: "grantedResources",
      render: (_text: string[], record: dealerSearchOutgoingHits) => (
        <Tooltip
          position="bottom"
          size="large"
          toolTipContent={record.grantedResources
            ?.map(resource => resource)
            .join(", ")}
        >
          <a
            href={`dealer/${record.id}/dashboard/resources`}
            rel="noreferrer"
            target="_blank"
          >{`${record.grantedResources.length}`}</a>
        </Tooltip>
      )
    },
    {
      title: "DealerCodes",
      dataIndex: "dealerCodes",
      render: (_text: string[], record: dealerSearchOutgoingHits) => (
        <Tooltip
          position="bottom"
          size="large"
          toolTipContent={record.dealerCodes
            ?.map(resource => resource.type + ": " + resource.code)
            .join(", ")}
        >
          <a
            href={`dealer/${record.id}/dashboard/dealerCodes`}
            rel="noreferrer"
            target="_blank"
          >{`${record.dealerCodes.length}`}</a>
        </Tooltip>
      )
    },
    {
      title: "Created",
      dataIndex: "created"
    }
  ];
  return retval;
}
